import { Head, router } from "@inertiajs/react"
import { BookOpen } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import {
  EntryDetailSheet,
  type EntryUpdatePayload,
  type ServerEntry,
} from "@/components/entry-detail-sheet"
import {
  EntryTimeline,
  type TimeGroup,
  type TimelineEntry,
} from "@/components/entry-timeline"
import { FigTree } from "@/components/fig-tree"
import { type WeekDay, WeekStrip } from "@/components/week-strip"
import AppLayout from "@/layouts/app-layout"
import { dashboardPath, entryPath, restoreEntryPath } from "@/routes"

// ── Types from server ────────────────────────────────────────────────────────

interface ServerWeekDay {
  date: string
  label: string
  day: number
  is_today: boolean
  has_entries: boolean
}

interface DaySummary {
  counts: Record<string, number>
  symptoms: string[]
  high_severity_symptoms: string[]
}

interface DashboardProps {
  selected_date: string
  week_days: ServerWeekDay[]
  entries: ServerEntry[]
  entry_count: number
  streak: { current: number; best: number }
  day_summary: DaySummary
  available_tags: { id: number; name: string }[]
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })
}

function formatSelectedDate(isoDate: string): string {
  // isoDate is a date string "2026-03-17" — parse with UTC noon to avoid
  // timezone-offset issues flipping the date
  const [year, month, day] = isoDate.split("-").map(Number)
  const d = new Date(year, month - 1, day)
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric" })
}

function isToday(isoDate: string): boolean {
  const today = new Date()
  const [year, month, day] = isoDate.split("-").map(Number)
  return (
    today.getFullYear() === year &&
    today.getMonth() + 1 === month &&
    today.getDate() === day
  )
}

/** Derive the display title for an entry from its kind and data */
function entryTitle(entry: ServerEntry): string {
  switch (entry.kind) {
    case "symptom":
      return typeof entry.data.symptom_type === "string" &&
        entry.data.symptom_type
        ? entry.data.symptom_type
        : "Symptom"
    case "meal": {
      const mealType = entry.data.meal_type as string | undefined
      if (mealType) {
        return mealType.charAt(0).toUpperCase() + mealType.slice(1)
      }
      return "Meal"
    }
    case "drink": {
      const drinkType = entry.data.drink_type as string | undefined
      if (drinkType) {
        return drinkType.charAt(0).toUpperCase() + drinkType.slice(1)
      }
      return "Drink"
    }
    case "stool": {
      const bristolType = entry.data.bristol_type as number | undefined
      if (bristolType !== undefined) {
        return `Stool · Type ${bristolType}`
      }
      return "Stool"
    }
    case "mood":
      return "Mood check-in"
    case "energy":
      return "Energy check-in"
    default:
      return entry.kind.charAt(0).toUpperCase() + entry.kind.slice(1)
  }
}

/** Derive the display description for an entry */
function entryDescription(entry: ServerEntry): string | undefined {
  switch (entry.kind) {
    case "meal": {
      const ingredients = entry.data.ingredients as string[] | undefined
      if (ingredients && ingredients.length > 0) {
        return ingredients
          .map((i) => i.charAt(0).toUpperCase() + i.slice(1))
          .join(", ")
      }
      return entry.notes ?? undefined
    }
    case "stool": {
      if (entry.notes) return entry.notes
      const description = entry.data.description as string | undefined
      if (description) return description
      return undefined
    }
    case "symptom": {
      if (entry.notes) return entry.notes
      const desc = entry.data.description as string | undefined
      return desc ?? undefined
    }
    default:
      return entry.notes ?? undefined
  }
}

/** Map a server entry kind to an EntryTimeline entry type */
function entryType(entry: ServerEntry): TimelineEntry["type"] {
  if (entry.kind === "drink") {
    const drinkType = entry.data.drink_type as string | undefined
    if (drinkType === "water") return "water"
    return "beverage"
  }
  return entry.kind
}

/** Group entries into morning / afternoon / evening TimeGroups */
function transformEntries(entries: ServerEntry[]): TimeGroup[] {
  const groups: Record<string, TimelineEntry[]> = {
    Evening: [],
    Afternoon: [],
    Morning: [],
  }

  for (const entry of entries) {
    const hour = new Date(entry.occurred_at).getHours()
    let groupLabel: string
    if (hour >= 17) {
      groupLabel = "Evening"
    } else if (hour >= 12) {
      groupLabel = "Afternoon"
    } else {
      groupLabel = "Morning"
    }

    const severityValue =
      entry.kind === "symptom" ? entry.data.severity : undefined
    const severity =
      typeof severityValue === "number" || typeof severityValue === "string"
        ? `${severityValue}/5`
        : undefined

    groups[groupLabel].push({
      id: entry.id,
      type: entryType(entry),
      title: entryTitle(entry),
      description: entryDescription(entry),
      severity,
      time: formatTime(entry.occurred_at),
      tags: entry.tags,
    })
  }

  return Object.entries(groups)
    .filter(([, entries]) => entries.length > 0)
    .map(([label, entries]) => ({ label, entries }))
}

// ── Component ────────────────────────────────────────────────────────────────

export default function Dashboard({
  selected_date,
  week_days,
  entries,
  entry_count,
  streak,
  day_summary,
  available_tags = [],
}: DashboardProps) {
  const weekDays: WeekDay[] = week_days.map((d) => ({
    label: d.label,
    date: d.date,
    day: d.day,
    isToday: d.is_today,
    hasEntries: d.has_entries,
  }))

  const timelineGroups = transformEntries(entries)
  const selectedIsToday = isToday(selected_date)

  const [selectedEntry, setSelectedEntry] = useState<ServerEntry | null>(null)

  const handleSelectDate = (date: string) => {
    router.get(
      dashboardPath(),
      { date },
      { preserveState: true, preserveScroll: true },
    )
  }

  const handleEntryClick = (id: string) => {
    const found = entries.find((e) => e.id === id)
    setSelectedEntry(found ?? null)
  }

  const handleSave = (id: string, payload: EntryUpdatePayload) => {
    router.patch(
      entryPath(id),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { entry: payload } as any,
      {
        preserveScroll: true,
        onSuccess: () => {
          setSelectedEntry(null)
          toast("Entry updated", { duration: 2000 })
        },
      },
    )
  }

  const handleDelete = (id: string) => {
    setSelectedEntry(null)
    router.delete(entryPath(id), {
      preserveScroll: true,
      onSuccess: () => {
        toast("Entry deleted", {
          action: {
            label: "Undo",
            onClick: () => {
              router.post(
                restoreEntryPath(id),
                {},
                {
                  preserveScroll: true,
                  onSuccess: () => toast("Entry restored", { duration: 2000 }),
                },
              )
            },
          },
          duration: 5000,
        })
      },
    })
  }

  return (
    <AppLayout>
      <Head title="Home" />

      <div className="px-5 pt-5 pb-[88px]">
        <WeekStrip
          days={weekDays}
          selectedDate={selected_date}
          onSelectDate={handleSelectDate}
        />

        <FigTree streakDays={streak.current} bestStreak={streak.best} />

        {/* Section label */}
        <div className="mb-3 flex items-center justify-between pl-[17px]">
          <span className="text-fig-text-inactive text-[13px] font-semibold tracking-[0.03em] uppercase">
            {selectedIsToday ? "Today" : formatSelectedDate(selected_date)}
          </span>
          <span className="text-fig-text-placeholder text-[12px]">
            {entry_count === 1 ? "1 entry" : `${entry_count} entries`}
          </span>
        </div>

        {timelineGroups.length > 0 ? (
          <EntryTimeline
            groups={timelineGroups}
            onEntryClick={handleEntryClick}
          />
        ) : selectedIsToday ? (
          <EmptyState
            icon={BookOpen}
            title="No entries yet"
            description="Tap the + button to log your first entry today."
          />
        ) : (
          <EmptyState
            icon={BookOpen}
            title="Nothing logged"
            description={`No entries were recorded on ${formatSelectedDate(selected_date)}.`}
          />
        )}
      </div>

      <EntryDetailSheet
        entry={selectedEntry}
        open={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onSave={handleSave}
        onDelete={handleDelete}
        availableTags={available_tags}
      />
    </AppLayout>
  )
}
